import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaPlus,
  FaEye,
  FaCog,
  FaEdit,
  FaTrash,
  FaClipboardList,
  FaBook,
  FaUser,
  FaClock,
  FaChartLine,
  FaRandom,
  FaProjectDiagram,
  FaChartPie,
  FaMagic,
} from "react-icons/fa";

import {
  useGetQuizzesQuery,
  useDeleteQuizMutation,
  useUpdateQuizActiveStatusMutation,
} from "../../slices/quizApiSlice";

const QuizListScreen = () => {
  const navigate = useNavigate();

  const {
    data: quizzes = [],
    isLoading,
    error,
    refetch,
  } = useGetQuizzesQuery();

  const [deleteQuiz, { isLoading: loadingDelete }] = useDeleteQuizMutation();

  const [updateQuizActiveStatus, { isLoading: loadingStatusUpdate }] =
    useUpdateQuizActiveStatusMutation();

  const getBlueprintRoute = (quiz) => {
    return quiz.hasBlueprint
      ? `/quizzes/${quiz._id}/blueprint/edit`
      : `/quizzes/${quiz._id}/blueprint/create`;
  };

  const toggleActiveHandler = async (quiz) => {
    try {
      await updateQuizActiveStatus({
        quizId: quiz._id,
        isActive: !quiz.isActive,
      }).unwrap();

      toast.success(
        `Quiz ${!quiz.isActive ? "activated" : "deactivated"} successfully`,
      );

      refetch();
    } catch (err) {
      toast.error(
        err?.data?.message || err?.error || "Failed to update quiz status",
      );
    }
  };

  const deleteHandler = async (id) => {
    if (window.confirm("Are you sure you want to delete this quiz?")) {
      try {
        await deleteQuiz(id).unwrap();

        toast.success("Quiz deleted successfully");

        refetch();
      } catch (err) {
        toast.error(
          err?.data?.message || err?.error || "Failed to delete quiz",
        );
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-lg text-gray-600">
        Loading quizzes...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="rounded-xl bg-red-100 text-red-700 p-5 shadow-lg">
          {error?.data?.message || error.error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-800">
              Quiz Management
            </h1>

            <p className="mt-2 text-gray-500">
              Manage quizzes for your coaching center
            </p>
          </div>

          <Link
            to="/quizzes/create"
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 font-semibold text-white shadow-lg hover:from-blue-700 hover:to-indigo-700 transition"
          >
            <FaPlus />
            Create Quiz
          </Link>
        </div>

        {loadingDelete && (
          <div className="mb-6 rounded-xl bg-blue-100 p-4 text-center text-blue-700">
            Deleting quiz...
          </div>
        )}

        {quizzes.length === 0 ? (
          <div className="rounded-3xl bg-white p-12 shadow-xl text-center">
            <FaClipboardList className="mx-auto text-6xl text-blue-600 mb-4" />

            <h2 className="text-2xl font-bold text-gray-700">
              No Quizzes Found
            </h2>

            <p className="mt-3 text-gray-500">
              Create your first quiz to start conducting online tests.
            </p>

            <Link
              to="/quizzes/create"
              className="inline-flex items-center gap-2 mt-6 rounded-xl bg-blue-600 px-6 py-3 text-white font-semibold hover:bg-blue-700 transition"
            >
              <FaPlus />
              Create Quiz
            </Link>
          </div>
        ) : (
          <div className="grid gap-6">
            {quizzes.map((quiz) => (
              <div
                key={quiz._id}
                className="rounded-3xl bg-white/90 backdrop-blur border border-gray-100 shadow-xl hover:shadow-2xl transition p-6"
              >
                <div className="flex flex-col lg:flex-row lg:justify-between gap-6">
                  {/* Quiz Details */}

                  <div className="flex-1">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-800">
                          {quiz.name}
                        </h2>

                        <p className="text-gray-500 mt-1">
                          {quiz.description || "No description available"}
                        </p>
                      </div>

                      <div className="flex gap-2 items-center">
                        <span
                          className={`rounded-full px-4 py-1 text-sm font-semibold ${
                            quiz.status === "published"
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {(quiz.status || "draft").toUpperCase()}
                        </span>

                        <span
                          className={`rounded-full px-4 py-1 text-sm font-semibold ${
                            quiz.isActive
                              ? "bg-blue-100 text-blue-700"
                              : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {quiz.isActive ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-6">
                      <div className="flex items-center gap-3">
                        <FaBook className="text-blue-600" />

                        <div>
                          <p className="text-xs uppercase text-gray-500">
                            Subject
                          </p>

                          <p className="font-semibold">{quiz.subject?.name}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <FaClock className="text-green-600" />

                        <div>
                          <p className="text-xs uppercase text-gray-500">
                            Duration
                          </p>

                          <p className="font-semibold">
                            {quiz.durationMinutes} mins
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <FaChartLine className="text-indigo-600" />

                        <div>
                          <p className="text-xs uppercase text-gray-500">
                            Passing
                          </p>

                          <p className="font-semibold">
                            {quiz.passingPercentage}%
                          </p>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs uppercase text-gray-500">
                          Difficulty
                        </p>

                        <span
                          className={`inline-block mt-1 rounded-full px-3 py-1 text-sm font-semibold ${
                            quiz.difficulty === "easy"
                              ? "bg-green-100 text-green-700"
                              : quiz.difficulty === "medium"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-red-100 text-red-700"
                          }`}
                        >
                          {quiz.difficulty}
                        </span>
                      </div>

                      <div className="flex items-center gap-3">
                        <FaRandom className="text-purple-600" />

                        <div>
                          <p className="text-xs uppercase text-gray-500">
                            Randomize
                          </p>

                          <p className="font-semibold">
                            {quiz.randomizeQuestions ? "Questions" : "No"}
                            {quiz.randomizeOptions ? " & Options" : ""}
                          </p>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs uppercase text-gray-500">
                          Attempts
                        </p>

                        <p className="font-semibold">
                          {quiz.allowMultipleAttempts
                            ? quiz.maxAttempts
                            : "Single"}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs uppercase text-gray-500">
                          Created By
                        </p>

                        <p className="flex items-center gap-2 font-semibold">
                          <FaUser className="text-blue-600" />
                          {quiz.createdBy?.name}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs uppercase text-gray-500">
                          Created On
                        </p>

                        <p className="font-semibold">
                          {new Date(quiz.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}

                  <div className="w-full space-y-4">
                    {/* Quiz Design */}
                    <div className="rounded-2xl border border-slate-200 p-3">
                      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-600 uppercase">
                        <FaCog />
                        Quiz Design
                      </h3>

                      <div className="flex flex-wrap gap-2">
                        {/* Questions */}
                       

                        {/* Blueprint */}
                        <button
                          onClick={() => navigate(getBlueprintRoute(quiz))}
                          className="flex items-center justify-center gap-2 rounded-xl bg-cyan-600 px-4 py-3 text-white hover:bg-cyan-700 transition"
                        >
                          <FaProjectDiagram />
                          {quiz.hasBlueprint
                            ? "Edit Blueprint"
                            : "Create Blueprint"}
                        </button>

                        
                        {/* <button
                          disabled={!quiz.hasBlueprint}
                          onClick={() =>
                            navigate(`/quizzes/${quiz._id}/generate`)
                          }
                          className={`flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-white transition ${
                            quiz.hasBlueprint
                              ? "bg-violet-600 hover:bg-violet-700"
                              : "bg-gray-300 cursor-not-allowed"
                          }`}
                        >
                          <FaMagic />
                          Generate
                        </button> */}
                      </div>
                    </div>

                   {/* Administration */}
<div className="rounded-2xl border border-slate-200 p-4 mt-4">
  <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-600 uppercase">
    <FaChartPie />
    Administration
  </h3>

  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">

    {/* Statistics */}
    <button
      disabled={!quiz.hasBlueprint}
      onClick={() =>
        navigate(`/quizzes/${quiz._id}/blueprint/statistics`)
      }
      className={`flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-white transition ${
        quiz.hasBlueprint
          ? "bg-emerald-600 hover:bg-emerald-700"
          : "bg-gray-300 cursor-not-allowed"
      }`}
    >
      <FaChartPie />
      Statistics
    </button>


    {/* View */}
    <button
      onClick={() => navigate(`/quizzes/${quiz._id}`)}
      className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-white hover:bg-blue-700 transition"
    >
      <FaEye />
      View
    </button>


    {/* Edit */}
    <button
      onClick={() => navigate(`/quizzes/${quiz._id}/edit`)}
      className="flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-4 py-3 text-white hover:bg-amber-600 transition"
    >
      <FaEdit />
      Edit
    </button>


    {/* Activate / Deactivate */}
    <button
      disabled={loadingStatusUpdate}
      onClick={() => toggleActiveHandler(quiz)}
      className={`flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-white transition ${
        quiz.isActive
          ? "bg-red-500 hover:bg-red-600"
          : "bg-green-600 hover:bg-green-700"
      } ${
        loadingStatusUpdate
          ? "opacity-50 cursor-not-allowed"
          : ""
      }`}
    >
      {quiz.isActive ? (
        <>
          <FaEye />
          Deactivate
        </>
      ) : (
        <>
          <FaEye />
          Activate
        </>
      )}
    </button>


    {/* Delete */}
    <button
      onClick={() => deleteHandler(quiz._id)}
      className="flex items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-3 text-white hover:bg-red-700 transition"
    >
      <FaTrash />
      Delete
    </button>

  </div>
</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizListScreen;
